import React from 'react';
import PropTypes from 'prop-types';
import { translationPropType } from '@eventbrite/i18n';
import { connect } from 'react-redux';
import _ from 'lodash';
import { formValidate } from '@eventbrite/eds-utils';
import { withMainControls } from '@eventbrite/eds-structure';
import { withFocusDrawerControls } from '@eventbrite/eds-structure';
import {
    registerFormHooks as registerFormHooksAction,
    releaseFormHooks as releaseFormHooksAction,
} from './actions';
import * as constants from './constants';
import { getNotificationFunction, getNotificationProps } from './utils';

// export for them public use and declare as constant for use within the file
export const {
    NOTIFICATION_TYPE_MAIN,
    NOTIFICATION_TYPE_PROPS,
    NOTIFICATION_DELAY,
    NOTIFICATION_TYPE_FOCUS_PANEL,
} = constants;

const connectFormHooks = connect(undefined, (dispatch) => ({
    registerFormHooks: (handleSubmit, reset, dirty) =>
        dispatch(registerFormHooksAction(handleSubmit, reset, dirty)),

    releaseFormHooks: () => dispatch(releaseFormHooksAction()),
}));

const checkForError = ({
    nextSubmitFailed,
    nextError,
    wasSubmitting,
    isSubmitting,
}) => nextSubmitFailed && nextError && wasSubmitting && !isSubmitting;

/**
 * Use this HOC to wrap your form component to use redux-form and to display form-level
 * notifications within EDS structure based on form state.
 * If you don't need the additional functionality of Structure Notifications or Form Hooks, you may
 * consider using `formValidate` directly.
 *
 * If form validation fails, a notification will be shown for 7 seconds telling the user
 * to look for invalid fields.
 *
 * If an error is populated by redux-form, a notification will be shown for 7 seconds with
 * the provided error message.
 *
 * @param {object} config - your redux-form configuration + additional configuration for this component.
 * @param {'withReduxForm/notification-type/main' | 'withReduxForm/notification-type/focus-panel'} [config.notificationType='withReduxForm/notification-type/main'] - changes type of notification shown when errors are displayed.
 * @param {boolean} [config.shouldPersist=false] - Whether or not the notification should persist and not auto-hide
 * @param {boolean} [config.useFormHooks=false] - if true, dispatches form hook information to provide outside parties some form control.  To take advantage of form hooks, include common/forms/reducers/formHooks in your project to intercept the dispatched actions.
 * @param {boolean} [config.useRemoteSubmit=false] - Whether or not you are manually calling submit by dispatching `submit` action fro `redux-form`
 * @returns {(React.Component) => React.Component} - A function that will wrap your form component with `redux-form` functionality
 */
const withReduxForm =
    ({
        notificationType = NOTIFICATION_TYPE_MAIN,
        shouldPersist = false,
        useFormHooks = false,
        useRemoteSubmit = false,
        ...config
    }) =>
    (Component) =>
        _.flowRight(
            formValidate(config),
            withMainControls,
            withFocusDrawerControls,
            connectFormHooks,
        )(
            class WithReduxForm extends React.Component {
                static propTypes = {
                    // following props provided by redux-form via formValidate
                    submitSucceeded: PropTypes.bool,
                    submitFailed: PropTypes.bool,
                    submitting: PropTypes.bool,
                    invalid: PropTypes.bool,
                    dirty: PropTypes.bool,
                    error: translationPropType,
                    handleSubmit: PropTypes.func,
                    reset: PropTypes.func,

                    // following props provided by withMainControls
                    hideMainNotification: PropTypes.func,
                    addMainNotification: PropTypes.func,

                    // following props provided by withFocusDrawerControls
                    addFocusDrawerNotification: PropTypes.func,
                    hideFocusDrawerNotification: PropTypes.func,

                    // following props provided by connection above
                    registerFormHooks: PropTypes.func,
                    releaseFormHooks: PropTypes.func,
                };

                state = {
                    shouldShowNotification: false,
                    errorMessage: undefined,
                };

                UNSAFE_componentWillReceiveProps(nextProps) {
                    const {
                        submitSucceeded: nextSubmitSucceeded,
                        submitFailed: nextSubmitFailed,
                        error: nextError,
                        submitting: isSubmitting,

                        registerFormHooks,
                        handleSubmit,
                        reset,
                        dirty,
                    } = nextProps;
                    const {
                        submitSucceeded: hadSubmitSucceeded,
                        submitFailed: hadSubmitFailed,
                        submitting: wasSubmitting,

                        dirty: wasDirty,
                    } = this.props;

                    // store any new error message in state instead of using it from
                    // props because on any form change, redux-form drops the error.
                    // we might want to continue displaying the error after the user
                    // changes their form.  only clear it when they click "Save" again.
                    if (nextError) {
                        this.setState({
                            errorMessage: nextError,
                        });
                    }

                    // if there is an error due to submit failure, and we just transitioned to this state.
                    // checking submitting prevents errors from appearing on other prop changes (such as
                    // focusing/blurring fields)
                    const hasNewError = checkForError({
                        nextSubmitFailed,
                        nextError,
                        wasSubmitting,
                        isSubmitting,
                    });

                    // If submit is manually being controlled, then create error message when nextError appears;
                    const hasRemoteSubmitError = useRemoteSubmit && nextError;

                    // if we just transitioned into a state of failed submission
                    const submitJustFailed =
                        nextSubmitFailed && !hadSubmitFailed;

                    // if we just transitioned into a state of successful submission
                    const submitJustSucceeded =
                        nextSubmitSucceeded && !hadSubmitSucceeded;

                    if (
                        submitJustFailed ||
                        hasNewError ||
                        hasRemoteSubmitError
                    ) {
                        this._showNotification();
                    }
                    if (submitJustSucceeded) {
                        this._hideNotification();
                    }

                    if (useFormHooks && wasDirty !== dirty) {
                        registerFormHooks(handleSubmit, reset, dirty);
                    }
                }

                componentDidUpdate() {
                    const {
                        shouldShowNotification,
                        shouldHideNotification,
                        errorMessage,
                    } = this.state;
                    const addNotification = this._getAddNotification();
                    const hideNotification = this._getHideNotification();
                    const originComponent =
                        notificationType === NOTIFICATION_TYPE_MAIN
                            ? Component.name
                            : undefined;

                    if (shouldShowNotification) {
                        addNotification(
                            getNotificationProps(errorMessage, {
                                shouldPersist,
                                onClose: this._hideNotification.bind(this),
                                ...(originComponent && { originComponent }),
                            }),
                        );
                    } else if (shouldHideNotification) {
                        // in this case the switch is assuring it does not get executed on next update.
                        // eslint-disable-next-line react/no-did-update-set-state
                        this.setState({ shouldHideNotification: false });
                        hideNotification(null, originComponent);
                    }
                }

                componentWillUnmount() {
                    const { releaseFormHooks } = this.props;
                    const hideNotification = this._getHideNotification();
                    const originComponent =
                        notificationType === NOTIFICATION_TYPE_MAIN
                            ? Component.name
                            : undefined;

                    hideNotification(null, originComponent);

                    if (useFormHooks) {
                        releaseFormHooks();
                    }
                }

                _showNotification() {
                    this.setState({
                        shouldShowNotification: true,
                        shouldHideNotification: false,
                    });
                }

                _hideNotification() {
                    this.setState({
                        shouldShowNotification: false,
                        shouldHideNotification: true,
                    });
                }

                _getAddNotification() {
                    const { addMainNotification, addFocusDrawerNotification } =
                        this.props;

                    return getNotificationFunction(
                        notificationType,
                        addMainNotification,
                        addFocusDrawerNotification,
                    );
                }

                _getHideNotification() {
                    const {
                        hideMainNotification,
                        hideFocusDrawerNotification,
                    } = this.props;

                    return getNotificationFunction(
                        notificationType,
                        hideMainNotification,
                        hideFocusDrawerNotification,
                    );
                }

                _handleSubmit(...params) {
                    const { invalid, submitFailed, handleSubmit } = this.props;

                    // clear any previously set error message when user attempts to save
                    this.setState({ errorMessage: undefined });

                    if (submitFailed && invalid) {
                        // the user is clicking save again, even though its
                        // already failed once and the form is still invalid
                        this._showNotification();

                        // need to short-circuit by returning early but should return
                        // result of handleSubmit otherwise to make this function
                        // transparent.  return undefined to bypass eslint consistent-return
                        return undefined;
                    }

                    return handleSubmit(...params);
                }

                _handleReset() {
                    const { reset } = this.props;

                    this._hideNotification();
                    return reset();
                }

                render() {
                    const { shouldShowNotification, errorMessage } = this.state;
                    let notificationProps;

                    if (
                        notificationType === NOTIFICATION_TYPE_PROPS &&
                        shouldShowNotification
                    ) {
                        notificationProps = {
                            formNotificationOptions:
                                getNotificationProps(errorMessage),
                        };
                    }

                    return (
                        <Component
                            {...this.props}
                            handleSubmit={this._handleSubmit.bind(this)}
                            reset={this._handleReset.bind(this)}
                            {...notificationProps}
                        />
                    );
                }
            },
        );

export default withReduxForm;
